import { Navigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * Componente para proteger rutas basándose en permisos
 */
export const ProtectedRoute = ({ user, children, requiredPermission }) => {
  const { hasPermission, hasAnyPermission, isAdmin } = usePermissions(user);

  // Admin tiene acceso a todo
  if (isAdmin) {
    return children;
  }

  // Si requiere un permiso específico
  if (requiredPermission) {
    if (hasPermission(requiredPermission)) {
      return children;
    }
    // Si no tiene el permiso, redirigir a la primera página permitida
    return <Navigate to="/dashboard" replace />;
  }

  // Si no requiere permiso específico, permitir acceso
  return children;
};

/**
 * Hook para obtener la ruta inicial según los permisos del usuario
 */
export const useInitialRoute = (user) => {
  const { hasAnyPermission, hasPermission, isAdmin } = usePermissions(user);

  if (!user) return '/login';
  if (isAdmin) return '/rrhh';

  // Verificar permisos en orden de prioridad
  
  // Agricultura - RRHH
  if (hasAnyPermission([
    'agricultura.rrhh.trabajadores.view',
    'agricultura.rrhh.expediente.view',
    'agricultura.rrhh.asistencia.view',
    'agricultura.rrhh.disciplinario.view'
  ])) {
    return '/rrhh';
  }

  // Agricultura - Producción
  if (hasPermission('agricultura.produccion.hoja_pesador.view')) {
    return '/produccion/hoja-pesador';
  }
  
  if (hasAnyPermission([
    'agricultura.produccion.lotes.view',
    'agricultura.produccion.registros.view',
    'agricultura.produccion.reportes.view',
    'agricultura.produccion.salud_seguridad.view',
    'agricultura.produccion.cronograma.view'
  ])) {
    return '/produccion';
  }

  // Agricultura - Maquinaria
  if (hasAnyPermission([
    'agricultura.maquinaria.inventario.view',
    'agricultura.maquinaria.inspecciones.view',
    'agricultura.maquinaria.uso.view',
    'agricultura.maquinaria.mantenimiento.view'
  ])) {
    return '/maquinaria';
  }

  // Agricultura - Admin
  if (hasAnyPermission([
    'agricultura.admin.empresas.view',
    'agricultura.admin.fincas.view',
    'agricultura.admin.usuarios.view',
    'agricultura.admin.historial.view',
    'agricultura.admin.tareas.view',
    'agricultura.admin.notificaciones.view'
  ])) {
    return '/administrativo';
  }

  // Ganadería
  if (hasAnyPermission([
    'ganaderia.inventario.animales.view',
    'ganaderia.peso.registros.view',
    'ganaderia.salud.tratamientos.view',
    'ganaderia.salud.diagnosticos.view',
    'ganaderia.reproduccion.embarazos.view',
    'ganaderia.reproduccion.genealogia.view',
    'ganaderia.dashboard.view'
  ])) {
    return '/ganaderia';
  }

  // Si no tiene permisos, redirigir a una página de "sin acceso"
  return '/no-access';
};
